/**
 * Оформление подписей.
 *
 * Отдельно от цвета и толщины линии: у надписи свои свойства — размер,
 * начертание, фон, рамка, перенос строк. Держать их в общей плашке
 * рядом с «толщиной линии» значило бы предлагать человеку настройки,
 * которые к тексту не относятся.
 */

import type { DrawingKind } from './kinds.js';

export interface TextStyle {
  readonly fontSize: number;
  readonly bold: boolean;
  readonly italic: boolean;
  /** Подложка под текстом: на пёстром графике без неё не читается. */
  readonly background: boolean;
  /**
   * Цвет подложки.
   *
   * Отдельной настройкой, а не цветом темы: тёмная подложка на тёмном
   * графике выглядит так, будто её не включили вовсе.
   */
  readonly backgroundColor: string;
  /** Плотность подложки, 0…1. */
  readonly backgroundOpacity: number;
  readonly border: boolean;
  /** Переносить длинную подпись по словам. */
  readonly wrap: boolean;
  /** Ширина, после которой начинается перенос, пикселей. */
  readonly wrapWidth: number;
  /**
   * Свой цвет текста. Нет — текст цветом самой фигуры: линия и её
   * подпись одного тона, пока человек не решит иначе.
   */
  readonly color?: string;
}

export const DEFAULT_TEXT_STYLE: TextStyle = {
  fontSize: 12,
  bold: false,
  italic: false,
  background: false,
  backgroundColor: '#0D1016',
  backgroundOpacity: 0.85,
  border: false,
  wrap: false,
  wrapWidth: 200,
};

/**
 * Оформление карточек — заметки, сноски, флага, плана сделки.
 *
 * У карточки есть своя поверхность, поэтому умолчания другие, чем у
 * голой надписи: граница включена, кегль чуть крупнее. «Фон» у
 * карточки — это свой цвет подложки вместо фирменного стекла.
 */
export const CARD_TEXT_STYLE: TextStyle = {
  ...DEFAULT_TEXT_STYLE,
  fontSize: 13,
  border: true,
  backgroundColor: '#141B30',
  backgroundOpacity: 0.94,
};

const CARD_KINDS = new Set<DrawingKind>(['note', 'callout', 'flag', 'trade-plan']);

/** Надпись живёт на карточке со своей поверхностью. */
export function isCard(kind: DrawingKind): boolean {
  return CARD_KINDS.has(kind);
}

/** У каких инструментов есть текст. */
export function hasText(kind: DrawingKind): boolean {
  return TEXT_KINDS.has(kind);
}

const TEXT_KINDS = new Set<DrawingKind>([
  'text',
  'note',
  'callout',
  'flag',
  'trade-plan',
  // Прямоугольник отмечает зону — подписать её хочется прямо в ней.
  'rect',
  // Стрелка без подписи — просто указание; с подписью она объясняет,
  // на что именно указывает.
  'arrow',
  'arrow-curve',
  'arrow-up',
  'arrow-down',
  // Линии, каналы и фигуры подписывают прямо на них (см. captions.ts).
  'trend',
  'ray',
  'extended',
  'info-line',
  'trend-angle',
  'horizontal',
  'horizontal-ray',
  'vertical',
  'parallel-channel',
  'flat-channel',
  'disjoint-channel',
  'regression',
  'circle',
  'ellipse',
  'path',
  'polyline',
]);

export function textStyleOf(stored: Partial<TextStyle> | undefined, kind?: DrawingKind): TextStyle {
  // Умолчания — по виду: у карточки своя поверхность и граница.
  const base = kind && CARD_KINDS.has(kind) ? CARD_TEXT_STYLE : DEFAULT_TEXT_STYLE;
  return stored ? { ...base, ...stored } : base;
}

/** Шрифт холста по настройкам подписи. */
export function fontOf(style: TextStyle): string {
  const weight = style.bold ? '600 ' : '';
  const slant = style.italic ? 'italic ' : '';
  return `${slant}${weight}${style.fontSize}px Onest, system-ui, sans-serif`;
}

/**
 * Разбивка подписи на строки.
 *
 * Переносим по словам и по явным переводам строки: человек, нажавший
 * Enter в поле ввода, ожидает увидеть перенос там же.
 */
export function wrapText(
  text: string,
  style: TextStyle,
  measure: (line: string) => number,
): string[] {
  const paragraphs = text.split('\n');
  if (!style.wrap) return paragraphs;

  const lines: string[] = [];
  for (const paragraph of paragraphs) {
    let current = '';
    for (const word of paragraph.split(' ')) {
      const candidate = current ? `${current} ${word}` : word;
      if (current && measure(candidate) > style.wrapWidth) {
        lines.push(current);
        current = word;
      } else {
        current = candidate;
      }
    }
    lines.push(current);
  }
  return lines;
}

/** Приведение сохранённого оформления к текущей форме. */
export function normalizeTextStyle(value: unknown): TextStyle | undefined {
  if (typeof value !== 'object' || value === null) return undefined;
  const raw = value as Partial<TextStyle>;

  return {
    ...DEFAULT_TEXT_STYLE,
    ...raw,
    // Нулевой и отрицательный кегль сделали бы подпись невидимой молча.
    fontSize:
      typeof raw.fontSize === 'number' && raw.fontSize >= 6 && raw.fontSize <= 72
        ? raw.fontSize
        : DEFAULT_TEXT_STYLE.fontSize,
    wrapWidth:
      typeof raw.wrapWidth === 'number' && raw.wrapWidth >= 40
        ? raw.wrapWidth
        : DEFAULT_TEXT_STYLE.wrapWidth,
    backgroundColor:
      typeof raw.backgroundColor === 'string'
        ? raw.backgroundColor
        : DEFAULT_TEXT_STYLE.backgroundColor,
    ...(typeof raw.color === 'string' && /^#([0-9a-f]{6}|[0-9a-f]{8})$/i.test(raw.color) ? { color: raw.color } : { color: undefined }),
    backgroundOpacity:
      typeof raw.backgroundOpacity === 'number' &&
      raw.backgroundOpacity >= 0 &&
      raw.backgroundOpacity <= 1
        ? raw.backgroundOpacity
        : DEFAULT_TEXT_STYLE.backgroundOpacity,
  };
}

export interface TextBox {
  readonly left: number;
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
  readonly lines: readonly string[];
  readonly lineHeight: number;
  readonly padding: number;
}

/**
 * Место, которое занимает подпись.
 *
 * Одна функция и для рисования, и для попадания курсором. Пока их было
 * две, они разошлись: нарисованная надпись занимала строку, а нажать
 * можно было только точку привязки — подпись нельзя было ни выделить,
 * ни сдвинуть.
 */
export function textBox(
  at: { x: number; y: number },
  text: string,
  style: TextStyle,
  measure: (line: string) => number,
): TextBox {
  const lines = wrapText(text, style, measure);
  const lineHeight = Math.round(style.fontSize * 1.35);
  const width = Math.max(8, ...lines.map((line) => measure(line)));
  const padding = style.background || style.border ? 6 : 2;

  // Отступ от опоры: кольцо в точке не должно ложиться на первую букву.
  const left = at.x + 11;
  const top = at.y - lineHeight / 2 - padding;
  const boxWidth = width + padding * 2;
  const boxHeight = lines.length * lineHeight + padding * 2 - (lineHeight - style.fontSize);

  return {
    left,
    top,
    right: left + boxWidth,
    bottom: top + boxHeight,
    lines,
    lineHeight,
    padding,
  };
}

/**
 * Мерка текста вне холста графика.
 *
 * Нужна обработчику мыши: он должен знать размеры подписи, не дожидаясь
 * следующего кадра отрисовки.
 */
let measuringContext: CanvasRenderingContext2D | null = null;

export function measurerFor(style: TextStyle): (line: string) => number {
  // Без DOM меряем на глаз по ширине знака моноширинного шрифта: это
  // приближение, но оно не даёт коду упасть там, где холста нет вовсе.
  const estimate = (line: string): number => line.length * style.fontSize * 0.6;
  if (typeof document === 'undefined') return estimate;

  if (!measuringContext) {
    measuringContext = document.createElement('canvas').getContext('2d');
  }
  const ctx = measuringContext;
  if (!ctx) return estimate;

  ctx.font = fontOf(style);
  return (line) => ctx.measureText(line).width;
}

/** Цвета подложки: тёмная, светлая и по одной на каждую роль. */
export const BACKGROUND_COLORS: readonly string[] = [
  '#0D1016',
  '#E7EAF0',
  '#6E8BFF',
  '#3FBF87',
  '#F0616F',
  '#5AC8D8',
];
